import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { portalSpendCategories, type PortalSpendResponse } from './portalSpend.types'

export function PortalSpendControls({
  params,
  change,
  data,
}: {
  params: URLSearchParams
  change: (key: string, value: string) => void
  data?: PortalSpendResponse['spend']
}) {
  const selected = new Set((params.get('categories') ?? '').split(',').filter(Boolean))
  const toggle = (key: string) => {
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    change('categories', [...next].join(','))
  }
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Input
          aria-label="Search invoices"
          placeholder="Invoice number or person"
          className="h-9 w-56"
          value={params.get('q') ?? ''}
          onChange={(event) => change('q', event.target.value)}
        />
        <FilterDropdown
          label="Paid or unpaid"
          emptyLabel="Paid or unpaid"
          options={[
            { value: 'paid', label: 'Paid' },
            { value: 'unpaid', label: 'Unpaid' },
          ]}
          selected={new Set((params.get('status') ?? '').split(',').filter(Boolean))}
          onChange={(next) => change('status', [...next].join(','))}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-2">
        {data?.categories
          .filter((category) => portalSpendCategories.includes(category.key))
          .map((category) => (
            <Button
              key={category.key}
              variant="outline"
              className={cn(
                'h-9 gap-1.5 rounded-full px-3 text-body-sm',
                selected.has(category.key) && 'border-primary/40 bg-primary/10',
              )}
              aria-pressed={selected.has(category.key)}
              onClick={() => toggle(category.key)}
            >
              {selected.has(category.key) && (
                <Check aria-hidden="true" className="size-3.5 text-primary" />
              )}
              <span>{category.label}</span>{' '}
              <span className="font-medium tabular-nums">{category.amount.formatted}</span>
            </Button>
          ))}
      </div>
    </>
  )
}
export function PortalSpendStats({
  name,
  stats,
}: {
  name: string
  stats: PortalSpendResponse['spend']['stats']
}) {
  const cells = [
    [`Billed to ${name}`, stats.organization],
    ['Billed to its people', stats.people],
    ['Unpaid', stats.unpaid],
  ] as const
  return (
    <dl className="grid grid-cols-3 divide-x divide-border border-b border-border">
      {cells.map(([caption, value]) => (
        <div key={caption} className="min-w-0 px-4 py-3">
          <dt className="text-caption text-muted-foreground">{caption}</dt>
          <dd className="text-body font-medium tabular-nums text-foreground">{value.formatted}</dd>
        </div>
      ))}
    </dl>
  )
}
